import { useState } from 'react';
import { Button, Card, Collapse } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import { getSeaLinkBasename, hasUnscheduledFerryTickets } from 'utils/helpers';
import { useNextDepartures } from './api';
import Departure from './departure';
import FerryTypeSwitcher from './ferry_type_switcher';

const regexPassengerFerry = /^passenger ferry$/i;
const regexVehicleFerry = /^ferry$/i;

const NextDepartures = (settings = {}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const hasFerryDisruption = useSelector(
    (state) => state.sealink.ferryDisruption
  );
  const nextDepartures = useSelector((state) => state.sealink.nextDepartures);
  const currentProductType = nextDepartures[selected].productType;
  let isUnscheduledTicket = hasUnscheduledFerryTickets();
  if (regexPassengerFerry.test(currentProductType)) {
    isUnscheduledTicket = true;
  }
  if (regexVehicleFerry.test(currentProductType)) {
    isUnscheduledTicket = false;
  }
  const [segmentIds, setSegmentIds] = useState(
    () => nextDepartures[0].segments
  );
  const [data, isLoading] = useNextDepartures(...segmentIds);
  const [toIslandService, fromIslandService] = data;

  const servicesUnavailable =
    isEmpty(toIslandService) && isEmpty(fromIslandService);

  const basename = getSeaLinkBasename();
  const {
    noDeparturesText = 'There are no more departures scheduled today.',
    upcomingLinkUrl = `/${basename}/bookings/ferry`,
    upcomingLinkText = 'Book an upcoming trip',
    hideCtaButton = false,
  } = settings;

  const renderFerryDisruptionsLink = (
    <a
      href={`/${basename}/ferry-status`}
      className="d-block btn btn-sm button-link text-danger bg-white"
    >
      <i className="fas fa-exclamation-circle icon__before-text" />
      View disruptions
    </a>
  );

  let buttonText = isUnscheduledTicket ? 'Buy tickets' : 'Book';
  if (hideCtaButton) {
    buttonText = '';
  }

  return (
    <Card className="departure-list overflow-hidden border-0 bg-primary">
      <Card.Header className="border-0 px-4 pb-2 pb-lg-0">
        <div className="d-flex flex-row justify-content-between align-items-end align-items-lg-center">
          <div className="d-flex flex-column-reverse flex-lg-row align-items-start align-items-lg-center">
            <Button
              variant="link"
              className="d-block text-white px-0 me-2"
              onClick={() => setOpen(!open)}
            >
              <span className="d-block d-lg-none text-large fw-normal">
                Next departures
                <i
                  className={classNames(
                    'fa icon__after-text text-xxx-small',
                    open ? 'fa-chevron-up' : 'fa-chevron-down'
                  )}
                />
              </span>
              <span className="d-none d-lg-block d-xl-none text-x-large fw-normal">
                Today&rsquo;s next departures
              </span>
              <span className="d-none d-xl-block text-xx-large fw-normal">
                Today&rsquo;s next departures
              </span>
            </Button>
            {hasFerryDisruption && renderFerryDisruptionsLink}
          </div>
          <Button
            variant="link"
            className="d-block text-white text-end button-link pe-0"
            href={`/${basename}/ferry-information/timetable`}
          >
            <span className="d-xl-none text-small">View timetables</span>
            <span className="d-none d-xl-inline">View timetables</span>
          </Button>
        </div>
      </Card.Header>
      <Collapse in={open}>
        <Card.Body className="d-lg-block p-0 mx-4 border-top">
          <div className="h-100 d-flex flex-column">
            <FerryTypeSwitcher
              options={nextDepartures}
              value={selected}
              onChange={(v) => {
                setSelected(v);
                setSegmentIds(nextDepartures[v].segments);
              }}
            />
            <div className="flex-grow-1 d-flex flex-column justify-content-center">
              {servicesUnavailable && !isLoading ? (
                <div className="d-flex flex-column py-primary py-lg-0 align-items-center text-white gap-2">
                  <div>{noDeparturesText}</div>
                  <Button
                    className="btn btn-accent text-white"
                    href={upcomingLinkUrl}
                  >
                    {upcomingLinkText}
                  </Button>
                </div>
              ) : (
                <div className="row mb-3">
                  {(!isEmpty(toIslandService) || isLoading) && (
                    <div className="departure-list__col col-12 col-lg-6">
                      <Departure
                        arrivalTime={toIslandService.arrivalTime}
                        arrivalDescription={toIslandService.arrivalDescription}
                        departureTime={toIslandService.departureTime}
                        departureDescription={
                          toIslandService.departureDescription
                        }
                        isBoarding={toIslandService.isBoarding}
                        isLoading={isLoading}
                        buttonHref={
                          isUnscheduledTicket
                            ? `/${basename}/bookings/ferry#passenger-ferry`
                            : `/${basename}/bookings/ferry?direction=to-island&date=${toIslandService.departureDate}`
                        }
                        buttonText={buttonText}
                      />
                    </div>
                  )}
                  {(!isEmpty(fromIslandService) || isLoading) && (
                    <div className="departure-list__col col-12 col-lg-6">
                      <Departure
                        arrivalTime={fromIslandService.arrivalTime}
                        arrivalDescription={
                          fromIslandService.arrivalDescription
                        }
                        departureTime={fromIslandService.departureTime}
                        departureDescription={
                          fromIslandService.departureDescription
                        }
                        isBoarding={fromIslandService.isBoarding}
                        isLoading={isLoading}
                        buttonHref={
                          isUnscheduledTicket
                            ? `/${basename}/bookings/ferry#passenger-ferry`
                            : `/${basename}/bookings/ferry?direction=from-island&date=${fromIslandService.departureDate}`
                        }
                        buttonText={buttonText}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default NextDepartures;
